import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export const VIM_PAY_W9_FORM_REVISION = '2024-03';
export const VIM_PAY_W9_IRS_URL = 'https://www.irs.gov/pub/irs-pdf/fw9.pdf';
export const VIM_PAY_W9_INSTRUCTIONS_URL = 'https://www.irs.gov/instructions/iw9';

const BLACK = rgb(0.05, 0.05, 0.05);
const GRAY = rgb(0.35, 0.35, 0.35);
const LIGHT = rgb(0.92, 0.92, 0.92);

function clean(value) {
  return String(value ?? '').trim();
}

function digits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

function formatTin(value, type) {
  const raw = digits(value).slice(0, 9);
  if (raw.length !== 9) return raw;
  if (type === 'ein') return `${raw.slice(0, 2)}-${raw.slice(2)}`;
  return `${raw.slice(0, 3)}-${raw.slice(3, 5)}-${raw.slice(5)}`;
}

function classificationLabel(values) {
  const labels = {
    individual: 'Individual / sole proprietor',
    c_corporation: 'C corporation',
    s_corporation: 'S corporation',
    partnership: 'Partnership',
    trust_estate: 'Trust / estate',
    llc: `LLC${values.llc_tax_classification ? ` (${values.llc_tax_classification})` : ''}`,
    other: `Other${values.other_classification ? `: ${values.other_classification}` : ''}`,
  };
  return labels[values.federal_tax_classification] || clean(values.federal_tax_classification);
}

function wrapText(text, font, size, width) {
  const words = clean(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= width || !line) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawWrapped(page, text, { x, y, width, font, size = 8, lineHeight = 10, color = BLACK }) {
  let cursor = y;
  for (const line of wrapText(text, font, size, width)) {
    page.drawText(line, { x, y: cursor, font, size, color });
    cursor -= lineHeight;
  }
  return cursor;
}

function drawField(page, label, value, { x, y, width, height = 31, font, bold }) {
  page.drawRectangle({ x, y: y - height, width, height, borderColor: BLACK, borderWidth: 0.7 });
  page.drawText(label, { x: x + 5, y: y - 10, font: bold, size: 7.3, color: BLACK });
  const lines = wrapText(clean(value) || '—', font, 9, width - 10).slice(0, 2);
  lines.forEach((line, index) => page.drawText(line, { x: x + 5, y: y - 22 - index * 9, font, size: 9, color: BLACK }));
  return y - height;
}

export function validateW9Builder(values = {}, tin = '', signatureName = '') {
  const errors = [];
  const classification = clean(values.federal_tax_classification);
  if (!clean(values.name_line1)) errors.push('Name on line 1 is required.');
  if (!classification) errors.push('Federal tax classification is required.');
  if (classification === 'llc' && !['C', 'S', 'P'].includes(values.llc_tax_classification)) errors.push('LLC tax classification C, S, or P is required.');
  if (classification === 'other' && !clean(values.other_classification)) errors.push('Describe the “Other” tax classification.');
  if (!clean(values.address_line1)) errors.push('Address is required.');
  if (!clean(values.city_state_zip)) errors.push('City, state, and ZIP are required.');
  if (!['ssn_itin', 'ein'].includes(values.tin_type)) errors.push('Choose SSN/ITIN or EIN.');
  if (digits(tin).length !== 9) errors.push('Enter a 9-digit taxpayer identification number.');
  if (!clean(signatureName)) errors.push('Type the electronic signature name.');
  if (!values.us_person_confirmed) errors.push('Confirm that the payee is a U.S. person eligible to provide Form W-9.');
  if (!values.certification_accepted) errors.push('Electronic certification is required.');
  if (values.subject_to_backup_withholding) errors.push('Because you indicated current IRS backup-withholding notification, use the manual W-9 upload option so certification item 2 can be crossed out as instructed by the IRS.');
  return errors;
}

export async function generateSubstituteW9Pdf({ values = {}, tin = '', signatureName = '', certifiedAt = new Date().toISOString() }) {
  const errors = validateW9Builder(values, tin, signatureName);
  if (errors.length) throw new Error(errors[0]);

  const pdf = await PDFDocument.create();
  pdf.setTitle('Substitute Form W-9 - VIM Pay');
  pdf.setSubject('Request for Taxpayer Identification Number and Certification');
  pdf.setAuthor('VIM Pay / VIM Media LLC');
  pdf.setCreator('VIM Pay secure W-9 builder');
  pdf.setProducer('VIM Pay');
  pdf.setCreationDate(new Date(certifiedAt));
  pdf.setModificationDate(new Date(certifiedAt));

  const page = pdf.addPage([612, 792]);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const margin = 36;
  const contentWidth = 540;
  let y = 756;

  page.drawText('Substitute Form W-9', { x: margin, y, font: bold, size: 20, color: BLACK });
  page.drawText('(Rev. March 2024)', { x: margin + 200, y: y + 2, font, size: 8.5, color: GRAY });
  y -= 24;
  page.drawText('Request for Taxpayer Identification Number and Certification', { x: margin, y, font: bold, size: 11, color: BLACK });
  page.drawText('Requester: VIM Media LLC', { x: 430, y, font, size: 8, color: BLACK });
  y -= 13;
  page.drawText('Give this form to the requester. Do not send it to the IRS.', { x: margin, y, font: bold, size: 8, color: BLACK });
  y -= 15;

  y = drawField(page, '1  Name of entity/individual. An entry is required.', values.name_line1, { x: margin, y, width: contentWidth, font, bold });
  y = drawField(page, '2  Business name/disregarded entity name, if different from above.', values.business_name, { x: margin, y, width: contentWidth, font, bold });

  const classHeight = 50;
  page.drawRectangle({ x: margin, y: y - classHeight, width: contentWidth, height: classHeight, borderColor: BLACK, borderWidth: 0.7 });
  page.drawText('3a  Federal tax classification - check only one.', { x: margin + 5, y: y - 11, font: bold, size: 7.3, color: BLACK });
  page.drawText(classificationLabel(values), { x: margin + 5, y: y - 25, font: bold, size: 9, color: BLACK });
  const flowThrough = ['partnership', 'trust_estate'].includes(values.federal_tax_classification)
    || (values.federal_tax_classification === 'llc' && values.llc_tax_classification === 'P');
  page.drawText(`3b  Foreign partners/owners/beneficiaries: ${flowThrough ? (values.has_foreign_partners ? 'Yes' : 'No') : 'Not applicable'}`, { x: margin + 5, y: y - 40, font, size: 8, color: BLACK });
  y -= classHeight;

  page.drawRectangle({ x: margin, y: y - 38, width: contentWidth, height: 38, borderColor: BLACK, borderWidth: 0.7 });
  page.drawText('4  Exemptions', { x: margin + 5, y: y - 11, font: bold, size: 7.3, color: BLACK });
  page.drawText(`Exempt payee code: ${clean(values.exempt_payee_code) || '—'}`, { x: margin + 5, y: y - 25, font, size: 8.5, color: BLACK });
  page.drawText(`FATCA exemption code: ${clean(values.fatca_exemption_code) || '—'}`, { x: margin + 270, y: y - 25, font, size: 8.5, color: BLACK });
  y -= 38;

  y = drawField(page, '5  Address (number, street, and apt. or suite no.)', values.address_line1, { x: margin, y, width: contentWidth, font, bold });
  y = drawField(page, '6  City, state, and ZIP code', values.city_state_zip, { x: margin, y, width: contentWidth, font, bold });
  y = drawField(page, '7  Account number(s) (optional)', values.account_numbers, { x: margin, y, width: contentWidth, font, bold });

  y -= 8;
  page.drawRectangle({ x: margin, y: y - 54, width: contentWidth, height: 54, borderColor: BLACK, borderWidth: 1 });
  page.drawRectangle({ x: margin, y: y - 16, width: 82, height: 16, color: BLACK });
  page.drawText('Part I', { x: margin + 5, y: y - 12, font: bold, size: 9, color: rgb(1, 1, 1) });
  page.drawText('Taxpayer Identification Number (TIN)', { x: margin + 90, y: y - 12, font: bold, size: 10, color: BLACK });
  page.drawText(values.tin_type === 'ein' ? 'Employer identification number' : 'Social security number / ITIN', { x: margin + 10, y: y - 34, font, size: 8, color: BLACK });
  page.drawText(formatTin(tin, values.tin_type), { x: margin + 270, y: y - 37, font: bold, size: 13, color: BLACK });
  y -= 62;

  page.drawRectangle({ x: margin, y: y - 184, width: contentWidth, height: 184, borderColor: BLACK, borderWidth: 1 });
  page.drawRectangle({ x: margin, y: y - 16, width: 82, height: 16, color: BLACK });
  page.drawText('Part II', { x: margin + 5, y: y - 12, font: bold, size: 9, color: rgb(1, 1, 1) });
  page.drawText('Certification', { x: margin + 90, y: y - 12, font: bold, size: 10, color: BLACK });
  let cy = y - 31;
  cy = drawWrapped(page, 'Under penalties of perjury, I certify that:', { x: margin + 10, y: cy, width: contentWidth - 20, font: bold, size: 8.2, lineHeight: 10 });
  const certifications = [
    '1. The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and',
    '2. I am not subject to backup withholding because I am exempt, I have not been notified by the IRS that I am subject to backup withholding for failure to report interest or dividends, or the IRS has notified me that I am no longer subject to backup withholding; and',
    '3. I am a U.S. citizen or other U.S. person; and',
    '4. The FATCA code entered on this form, if any, indicating that I am exempt from FATCA reporting is correct.',
  ];
  certifications.forEach((statement) => {
    cy = drawWrapped(page, statement, { x: margin + 10, y: cy - 2, width: contentWidth - 20, font, size: 7.4, lineHeight: 9 });
  });
  cy -= 3;
  page.drawText('Electronic signature:', { x: margin + 10, y: cy, font: bold, size: 8, color: BLACK });
  page.drawText(`/s/ ${clean(signatureName)}`, { x: margin + 105, y: cy, font: bold, size: 9, color: BLACK });
  page.drawText(`Date: ${new Date(certifiedAt).toLocaleDateString('en-US')}`, { x: margin + 390, y: cy, font, size: 8, color: BLACK });
  cy -= 15;
  page.drawText('Authenticated electronic submission through VIM Pay. Certification accepted under penalties of perjury.', { x: margin + 10, y: cy, font, size: 6.8, color: GRAY });
  y -= 192;

  page.drawRectangle({ x: margin, y: y - 42, width: contentWidth, height: 42, color: LIGHT, borderColor: rgb(0.75, 0.75, 0.75), borderWidth: 0.5 });
  drawWrapped(page, 'Electronic substitute Form W-9 generated by VIM Pay. Form revision 2024-03. The signed PDF is the tax record; VIM Pay does not store the TIN in a normal application database field.', { x: margin + 7, y: y - 12, width: contentWidth - 14, font, size: 7, lineHeight: 9, color: GRAY });

  const bytes = await pdf.save({ useObjectStreams: false });
  return new Blob([bytes], { type: 'application/pdf' });
}
